import { useState, useEffect, useMemo, useCallback } from "react";

/**
 * Admin Tickets Management
 * View and manage all time tickets - complete orphaned tickets, delete bad entries
 * Admin only (mark.herman@3dcolor.com)
 */
export default function AdminTickets({ onBackToApp }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter controls
  const [stateFilter, setStateFilter] = useState("active"); // active, completed, all
  const [searchQuery, setSearchQuery] = useState("");

  // Selection for bulk actions
  const [selectedTickets, setSelectedTickets] = useState(new Set());

  // Complete modal state
  const [completeModal, setCompleteModal] = useState({
    open: false,
    ticket: null,
    hours: 0,
    minutes: 0,
    piecesFinished: 0,
  });

  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Tick for live timer updates (same pattern as MyWork)
  const [tick, setTick] = useState(0);

  // Tick interval for live timers
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Load tickets on mount and when filter changes
  useEffect(() => {
    loadTickets();
  }, [stateFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/admin/tickets?state=${stateFilter}&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load tickets");

      const data = await res.json();
      setTickets(data.tickets || []);
      setError(null);
    } catch (err) {
      console.error("Error loading tickets:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Force re-render on tick for live timers (same pattern as MyWork)
  void tick;

  // Format elapsed time - calculates fresh from startTime + accumulatedSeconds
  // This is the same logic used in MyWork that works correctly
  const formatElapsed = useCallback((ticket) => {
    if (!ticket) return "0:00:00";

    // Get accumulated time from previous pause/resume cycles
    const accumulated = Math.floor(ticket.accumulatedSeconds || 0);
    let seconds = 0;

    if (ticket.state === "running" && ticket.startTime) {
      // Running: calculate current segment + accumulated
      const startMs = new Date(ticket.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      seconds = accumulated + currentSegment;
    } else if (ticket.state === "completed" && ticket.durationSeconds) {
      // Completed: use final duration
      seconds = Math.floor(ticket.durationSeconds);
    } else {
      // Paused or other: use accumulated
      seconds = accumulated;
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    // Always show hh:mm:ss format
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, []);

  // Get running time in seconds (for orphan detection and modal)
  const getRunningTime = (ticket) => {
    if (!ticket) return 0;
    const accumulated = Math.floor(ticket.accumulatedSeconds || 0);

    if (ticket.state === "running" && ticket.startTime) {
      const startMs = new Date(ticket.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      return accumulated + currentSegment;
    } else if (ticket.state === "completed" && ticket.durationSeconds) {
      return Math.floor(ticket.durationSeconds);
    }
    return accumulated;
  };

  // Check if ticket is potentially orphaned (> 8 hours)
  const isOrphaned = (ticket) => {
    if (ticket.state === "completed") return false;
    const runningTime = getRunningTime(ticket);
    return runningTime > 8 * 3600; // > 8 hours
  };

  // Filter tickets
  const filteredTickets = useMemo(() => {
    let result = tickets;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.employeeName?.toLowerCase().includes(query) ||
          t.jobNumber?.toLowerCase().includes(query) ||
          t.jobName?.toLowerCase().includes(query) ||
          t.quoteNumber?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [tickets, searchQuery]);

  // Handle complete ticket
  const handleComplete = async () => {
    if (!completeModal.ticket) return;

    setProcessing(true);
    try {
      const durationSeconds =
        completeModal.hours * 3600 + completeModal.minutes * 60;

      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(
        `/api/admin/tickets/${completeModal.ticket.id}/complete`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            durationSeconds,
            piecesFinished: completeModal.piecesFinished || null,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to complete ticket");
      }

      // Refresh list
      await loadTickets();
      setCompleteModal({ open: false, ticket: null, hours: 0, minutes: 0, piecesFinished: 0 });
    } catch (err) {
      console.error("Error completing ticket:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Handle delete ticket
  const handleDelete = async (ticketId) => {
    setProcessing(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/time/tickets/${ticketId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete ticket");
      }

      // Remove from local state
      setTickets((prev) => prev.filter((t) => t.id !== ticketId));
      setSelectedTickets((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting ticket:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Open complete modal with pre-filled duration
  const openCompleteModal = (ticket) => {
    const runningTime = getRunningTime(ticket);
    const hours = Math.floor(runningTime / 3600);
    const minutes = Math.floor((runningTime % 3600) / 60);

    setCompleteModal({
      open: true,
      ticket,
      hours,
      minutes,
      piecesFinished: ticket.qty || 0,
    });
  };

  // Toggle ticket selection
  const toggleSelection = (ticketId) => {
    setSelectedTickets((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  // Select all visible active tickets
  const selectAllActive = () => {
    const activeIds = filteredTickets
      .filter((t) => t.state !== "completed")
      .map((t) => t.id);
    setSelectedTickets(new Set(activeIds));
  };

  // Get status badge
  const getStatusBadge = (state) => {
    if (state === "running") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded">
          Running
        </span>
      );
    }
    if (state === "paused") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-500/20 text-yellow-400 rounded">
          Paused
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium bg-slate-200/20 text-gray-500 rounded">
        Completed
      </span>
    );
  };

  return (
    <div className="flex-1 bg-slate-200 overflow-auto">
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* State filter */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            {[
              { key: "active", label: "Active" },
              { key: "completed", label: "Completed" },
              { key: "all", label: "All" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStateFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  stateFilter === key
                    ? "bg-purple-600 text-gray-900"
                    : "bg-slate-800 text-gray-500 hover:text-gray-900"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search employee, job, quote..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1.5 text-sm bg-slate-800 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-purple-500 w-64"
          />

          {/* Bulk actions */}
          {selectedTickets.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500">
                {selectedTickets.size} selected
              </span>
              <button
                onClick={() => setSelectedTickets(new Set())}
                className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 mb-4 text-red-400">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="text-gray-500">
            Total:{" "}
            <span className="text-gray-900 font-medium">
              {filteredTickets.length}
            </span>
          </div>
          <div className="text-gray-500">
            Orphaned (&gt;8h):{" "}
            <span className="text-red-400 font-medium">
              {filteredTickets.filter(isOrphaned).length}
            </span>
          </div>
        </div>

        {/* Tickets table */}
        <div className="bg-slate-800/50 rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700/50 text-gray-700 text-left">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAllActive();
                      } else {
                        setSelectedTickets(new Set());
                      }
                    }}
                    checked={
                      selectedTickets.size > 0 &&
                      filteredTickets
                        .filter((t) => t.state !== "completed")
                        .every((t) => selectedTickets.has(t.id))
                    }
                    className="accent-purple-500"
                  />
                </th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">Job #</th>
                <th className="px-3 py-2">Quote</th>
                <th className="px-3 py-2">Operation</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Loading tickets...
                  </td>
                </tr>
              )}
              {!loading && filteredTickets.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No tickets found
                  </td>
                </tr>
              )}
              {!loading &&
                filteredTickets.map((ticket) => {
                  const orphaned = isOrphaned(ticket);

                  return (
                    <tr
                      key={ticket.id}
                      className={`hover:bg-slate-200 ${
                        orphaned ? "bg-red-500/10" : ""
                      }`}
                    >
                      <td className="px-3 py-2">
                        {ticket.state !== "completed" && (
                          <input
                            type="checkbox"
                            checked={selectedTickets.has(ticket.id)}
                            onChange={() => toggleSelection(ticket.id)}
                            className="accent-purple-500"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(ticket.state)}
                          {orphaned && (
                            <span className="text-xs text-red-400" title="Possibly orphaned">
                              !!
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {ticket.employeeName}
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          <div className="font-mono">{ticket.jobNumber}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[150px]" title={ticket.jobName}>
                            {ticket.jobName}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {ticket.quoteNumber}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-blue-400">{ticket.operationCode}</span>
                        {ticket.workCenter && (
                          <span className="text-gray-500 text-xs ml-1">
                            ({ticket.workCenter})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {ticket.startTime
                          ? new Date(ticket.startTime).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`font-mono ${
                            orphaned
                              ? "text-red-400"
                              : ticket.state === "running"
                              ? "text-green-400"
                              : "text-gray-700"
                          }`}
                        >
                          {formatElapsed(ticket)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          {ticket.state !== "completed" && (
                            <button
                              onClick={() => openCompleteModal(ticket)}
                              className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 rounded transition-colors"
                            >
                              Complete
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(ticket.id)}
                            className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 rounded transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Complete Modal */}
      {completeModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md border border-gray-200">
            <h2 className="text-lg font-semibold mb-4">Complete Ticket</h2>

            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Employee</div>
                <div className="font-medium">{completeModal.ticket?.employeeName}</div>
              </div>

              <div>
                <div className="text-sm text-gray-500 mb-1">Job</div>
                <div className="font-medium">
                  {completeModal.ticket?.jobNumber} - {completeModal.ticket?.jobName}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">
                  Duration
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={completeModal.hours}
                    onChange={(e) =>
                      setCompleteModal((prev) => ({
                        ...prev,
                        hours: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-20 px-3 py-2 bg-slate-700 border border-gray-200 rounded text-gray-900 text-center"
                    min={0}
                  />
                  <span className="text-gray-500">hours</span>
                  <input
                    type="number"
                    value={completeModal.minutes}
                    onChange={(e) =>
                      setCompleteModal((prev) => ({
                        ...prev,
                        minutes: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-20 px-3 py-2 bg-slate-700 border border-gray-200 rounded text-gray-900 text-center"
                    min={0}
                    max={59}
                  />
                  <span className="text-gray-500">minutes</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">
                  Pieces Finished (optional)
                </label>
                <input
                  type="number"
                  value={completeModal.piecesFinished}
                  onChange={(e) =>
                    setCompleteModal((prev) => ({
                      ...prev,
                      piecesFinished: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-32 px-3 py-2 bg-slate-700 border border-gray-200 rounded text-gray-900"
                  min={0}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() =>
                  setCompleteModal({ open: false, ticket: null, hours: 0, minutes: 0, piecesFinished: 0 })
                }
                disabled={processing}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={processing}
                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 rounded transition-colors"
              >
                {processing ? "Completing..." : "Complete Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-sm border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 text-red-400">
              Delete Ticket?
            </h2>
            <p className="text-gray-500 mb-6">
              This will permanently delete this time ticket. This action cannot
              be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={processing}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={processing}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                {processing ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
